import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { Ionicons } from '@expo/vector-icons';

import MaskInput from 'react-native-mask-input';
import { ClientAddressField } from '@/components/schedule/client-address-field';
import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import {
  clearStoredOtpToken,
  getStoredOtpToken,
  getStoredGuestAddress,
  storeGuestAddress,
  storeGuestIdentity,
  useCreateGuestAppointment,
  useRequestGuestOtp,
  useVerifyGuestOtp,
  type GuestAppointmentResult,
} from '@/hooks/use-public-schedule';
import { useToastStore } from '@/store/toastStore';
import { brazilPhoneMask, formatBrazilPhone, getBrazilPhoneError } from '@/utils/brazil-phone';
import { AvailabilitySlot } from '@/types/appointment';
import { formatSlotTime } from '@/utils/format-appointment';
import { getApiErrorMessage } from '@/utils/api-error';
import { useThemedHex } from '@/hooks/use-theme-colors';

type Step = 'identify' | 'code';

interface GuestBookingSheetProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  /** Profissional da equipe escolhido no "Com quem?"; nulo = dono do slug. */
  professionalId?: string | null;
  serviceId: string;
  slot: AvailabilitySlot | null;
  brandColor: string;
  /** Serviço em que o profissional vai até o cliente: pergunta onde. */
  askAddress?: boolean;
  onSuccess: (result: GuestAppointmentResult) => void;
}

/**
 * Identificação do convidado no fluxo público (A2/B1): nome + telefone,
 * com verificação OTP por WhatsApp quando o telefone ainda não foi validado
 * neste device.
 */
export function GuestBookingSheet({
  open,
  onClose,
  slug,
  professionalId,
  serviceId,
  slot,
  brandColor,
  askAddress = false,
  onSuccess,
}: GuestBookingSheetProps) {
  const th = useThemedHex();
  const { addToast } = useToastStore();
  const [step, setStep] = useState<Step>('identify');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [address, setAddress] = useState('');
  const [savedAddress, setSavedAddress] = useState<string | null>(null);

  useEffect(() => {
    if (open && askAddress) void getStoredGuestAddress().then(setSavedAddress);
  }, [open, askAddress]);

  const typedAddress = askAddress ? address.trim() || undefined : undefined;
  const [submitting, setSubmitting] = useState(false);
  // O toast do app vive na raiz, atrás deste Modal: erro aqui tem que ser inline.
  const [error, setError] = useState<string | null>(null);

  const requestOtp = useRequestGuestOtp(slug, professionalId);
  const verifyOtp = useVerifyGuestOtp(slug);
  const createAppointment = useCreateGuestAppointment(slug, professionalId);

  const reset = () => {
    setStep('identify');
    setCode('');
    setError(null);
    setSubmitting(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const messageOf = (err: unknown, fallback: string): string => getApiErrorMessage(err, fallback);

  /** Manda o código e leva ao passo de verificação. */
  const sendCode = async (): Promise<boolean> => {
    try {
      const { devCode } = await requestOtp.mutateAsync(phone.trim());
      if (devCode) setCode(devCode);
      setStep('code');
      setError(null);
      return true;
    } catch (err: any) {
      setError(messageOf(err, 'Não conseguimos enviar o código. Confira o telefone.'));
      return false;
    }
  };

  const create = async (otpToken: string) => {
    if (!slot) return;
    try {
      const result = await createAppointment.mutateAsync({
        serviceId,
        startAt: slot.startAt,
        guest: { name: name.trim(), phone: phone.trim() },
        otpToken,
        address: typedAddress,
      });
      await storeGuestIdentity({ name: name.trim(), phone: phone.trim() });
      if (typedAddress) await storeGuestAddress(typedAddress);
      reset();
      onSuccess(result);
    } catch (err: any) {
      // Token do device expirado/inválido: refaz a verificação em vez de travar.
      if (err?.response?.status === 401) {
        await clearStoredOtpToken(phone.trim());
        const sent = await sendCode();
        if (sent) {
          setError('Sua verificação expirou. Confirme o novo código.');
        }
        return;
      }
      setError(messageOf(err, 'Não conseguimos marcar. Tente outro horário.'));
    }
  };

  const handleContinue = async () => {
    if (!name.trim()) {
      setError('Informe seu nome.');
      return;
    }
    // Mesma regra da API: DDD existente e celular, senão o código não chega.
    const phoneError = getBrazilPhoneError(phone);
    if (phoneError) {
      setError(phoneError);
      return;
    }
    setError(null);
    setSubmitting(true);
    // Telefone já verificado neste device agenda direto; senão, pede o código
    // antes — nada de tentar criar só para tomar 401.
    const stored = await getStoredOtpToken(phone.trim());
    if (stored) {
      await create(stored);
    } else {
      await sendCode();
    }
    setSubmitting(false);
  };

  const handleVerifyCode = async () => {
    if (code.trim().length !== 6) {
      setError('Digite o código de 6 dígitos.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { otpToken } = await verifyOtp.mutateAsync({
        phone: phone.trim(),
        code: code.trim(),
      });
      await create(otpToken);
    } catch (err: any) {
      setError(messageOf(err, 'Código incorreto.'));
    } finally {
      setSubmitting(false);
    }
  };

  const busy =
    submitting || requestOtp.isPending || verifyOtp.isPending || createAppointment.isPending;

  return (
    <Modal visible={open} transparent animationType="slide" onRequestClose={close}>
      <Pressable className="flex-1 justify-end bg-black/50" onPress={close}>
        <KeyboardAvoidingView behavior="padding">
          <Pressable
            onPress={(e) => e.stopPropagation()}
            className="rounded-t-3xl bg-card p-6 pb-10"
            style={{ borderCurve: 'continuous' }}
          >
            <View className="mb-5 flex-row items-center justify-between">
              <ThemedText className="font-bold text-xl">
                {step === 'identify' ? 'Quase lá!' : 'Confirme seu telefone'}
              </ThemedText>
              <Pressable
                onPress={close}
                className="h-9 w-9 items-center justify-center rounded-full bg-neutral-100"
                accessibilityLabel="Fechar"
              >
                <Ionicons className="text-neutral-600" name="close" size={18} />
              </Pressable>
            </View>

            {slot && (
              <View
                className="mb-5 flex-row items-center gap-2 self-start rounded-full px-4 py-2"
                style={{ backgroundColor: `${brandColor}14` }}
              >
                <Ionicons name="calendar-outline" size={15} color={brandColor} />
                <ThemedText className="font-semibold text-sm" style={{ color: brandColor }}>
                  {formatSlotTime(slot.startAt)}
                </ThemedText>
              </View>
            )}

            {!!error && (
              <View className="mb-4 flex-row items-start gap-2 rounded-2xl bg-red-50 p-3">
                <Ionicons className="text-error-600" name="alert-circle" size={17} />
                <ThemedText className="flex-1 text-sm leading-5 text-red-700">{error}</ThemedText>
              </View>
            )}

            {step === 'identify' ? (
              <View className="gap-3">
                <ThemedText className="text-sm text-neutral-500">
                  Só precisamos do seu nome e WhatsApp para confirmar o horário — sem criar conta.
                </ThemedText>
                {askAddress && (
                  <ClientAddressField
                    value={address}
                    onChange={setAddress}
                    saved={savedAddress}
                    editable={!busy}
                  />
                )}
                <Input
                  placeholder="Seu nome"
                  value={name}
                  onChangeText={setName}
                  autoCapitalize="words"
                  autoComplete="name"
                  editable={!busy}
                />
                <MaskInput
                  placeholder="WhatsApp com DDD (ex.: (11) 99999-9999)"
                  placeholderTextColor="#A3A3A3"
                  value={phone}
                  onChangeText={(_masked, unmasked) => setPhone(unmasked)}
                  mask={brazilPhoneMask}
                  keyboardType="phone-pad"
                  autoComplete="tel"
                  editable={!busy}
                  style={{
                    height: 48,
                    width: '100%',
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: th('#E5E5E5'),
                    backgroundColor: th('#FFFFFF'),
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    fontSize: 16,
                    color: th('#0A0A0A'),
                    borderCurve: 'continuous',
                  }}
                />
                <PressableScale
                  haptic
                  disabled={busy}
                  onPress={handleContinue}
                  className="mt-2 w-full flex-row items-center justify-center gap-2 rounded-full py-4"
                  style={{ backgroundColor: brandColor, borderCurve: 'continuous' }}
                >
                  {busy ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <ThemedText className="font-bold text-base text-white">
                      Confirmar horário
                    </ThemedText>
                  )}
                </PressableScale>
              </View>
            ) : (
              <View className="gap-3">
                <ThemedText className="text-sm text-neutral-500">
                  Enviamos um código de 6 dígitos para o WhatsApp{' '}
                  <ThemedText className="font-semibold text-sm text-neutral-700">
                    {formatBrazilPhone(phone)}
                  </ThemedText>
                  . Digite-o abaixo para confirmar o agendamento.
                </ThemedText>
                <Input
                  placeholder="000000"
                  value={code}
                  onChangeText={setCode}
                  keyboardType="number-pad"
                  maxLength={6}
                  className="text-center text-2xl tracking-[8px]"
                  editable={!busy}
                />
                <PressableScale
                  haptic
                  disabled={busy}
                  onPress={handleVerifyCode}
                  className="mt-2 w-full flex-row items-center justify-center gap-2 rounded-full py-4"
                  style={{ backgroundColor: brandColor, borderCurve: 'continuous' }}
                >
                  {busy ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <ThemedText className="font-bold text-base text-white">
                      Validar código
                    </ThemedText>
                  )}
                </PressableScale>
                <View className="flex-row items-center justify-center gap-6">
                  <Pressable
                    disabled={busy}
                    onPress={async () => {
                      setCode('');
                      const sent = await sendCode();
                      if (sent) addToast('Código reenviado!', 'success');
                    }}
                    className="py-2"
                  >
                    <ThemedText className="font-semibold text-sm text-neutral-500">
                      Reenviar código
                    </ThemedText>
                  </Pressable>
                  <Pressable
                    disabled={busy}
                    onPress={() => {
                      setCode('');
                      setError(null);
                      setStep('identify');
                    }}
                    className="py-2"
                  >
                    <ThemedText className="font-semibold text-sm text-neutral-500">
                      Trocar telefone
                    </ThemedText>
                  </Pressable>
                </View>
              </View>
            )}
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
    </Modal>
  );
}
